'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import type { CommunityChannelMessageAttachment } from '@ps/models/lib/community-channel';
import { cn } from '@ps/ui/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PhotoProvider } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

import { useCommunityMessageContext } from '../../../contexts/community-message/useCommunityMessageContext';
import type { ChannelMessageMediaInfo } from '../../../types/ChannelMessageMediaInfo';
import type { ExtendedCommunityChannelMessage } from '../../../types/ExtendedCommunityChannelMessage';
import { ChannelMessageAttachmentTile } from './ChannelMessageAttachmentTile';
import { ActiveSlideContext } from './LightboxVideo';

interface Props {
  message: ExtendedCommunityChannelMessage;
}

/**
 * The attachment strip rendered under a message body in the channel
 * bubble. Reads from two sources, in priority order:
 *
 * 1. **Remote** — `message.attachments` populated by the Firestore
 *    confirmation. This is the steady state once the send round trip
 *    has completed.
 * 2. **Local pending** — entries on the community context's
 *    `pendingAttachments$` keyed by the message's `clientMessageId`.
 *    Used for the brief window between optimistic insert and
 *    Firestore confirmation; renders the local file previews with a
 *    progress-ring overlay.
 *
 * Horizontal scroll with chevron nudge buttons mirrors the composer
 * strip on the editor side.
 */
export function ChannelMessageAttachments({ message }: Props) {
  const { medias$ } = useCommunityMessageContext();
  const [medias] = useBehaviorSubject(medias$);

  const localPending = useMemo<ChannelMessageMediaInfo[]>(
    () =>
      medias.filter(entry => entry.clientMessageId === message.clientMessageId),
    [medias, message.clientMessageId],
  );

  const remote = message.content.attachments ?? [];
  const tiles = useMemo<TileItem[]>(
    () =>
      remote.length > 0
        ? remote.map(toRemoteTile)
        : localPending.map(toLocalTile),
    [remote, localPending],
  );

  if (tiles.length === 0) return null;

  return <AttachmentStrip tiles={tiles} />;
}

type TileItem =
  | {
      kind: 'remote';
      key: string;
      filename: string;
      contentType: string;
      url: string;
    }
  | {
      kind: 'local';
      key: string;
      filename: string;
      contentType: string;
      localPreviewUrl: string;
      progress: number | undefined;
      errorMessage: string | undefined;
    };

function toRemoteTile(
  item: CommunityChannelMessageAttachment,
  index: number,
): TileItem {
  return {
    kind: 'remote',
    key: `${item.objectId ?? item.url}-${index}`,
    filename: item.filename,
    contentType: item.contentType,
    url: item.processedUrl ?? item.url,
  };
}

function toLocalTile(item: ChannelMessageMediaInfo): TileItem {
  // `source.type === 'remote'` shouldn't normally render through this
  // function — the bubble swaps to the message's confirmed
  // attachments once Firestore lands — but for the brief window
  // between upload-finished and Firestore-confirmation we still
  // render the remote URL.
  if (item.source.type === 'remote') {
    return {
      kind: 'local',
      key: item.key,
      filename: '',
      contentType: item.source.contentType,
      localPreviewUrl: item.source.url,
      progress: undefined,
      errorMessage: undefined,
    };
  }
  const state = item.source.state;
  return {
    kind: 'local',
    key: item.key,
    filename: item.source.filename,
    contentType: item.source.contentType,
    localPreviewUrl: item.source.localFileUri,
    progress: state.status === 'uploading' ? state.progress : undefined,
    errorMessage: state.status === 'error' ? state.error.message : undefined,
  };
}

function isVideoTile(tile: TileItem): boolean {
  return tile.contentType.startsWith('video/');
}

function tileMediaUrl(tile: TileItem): string {
  return tile.kind === 'remote' ? tile.url : tile.localPreviewUrl;
}

function AttachmentStrip({ tiles }: { tiles: TileItem[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Which slide the lightbox is showing (registration order === tile order,
  // since every remote tile is a `PhotoView`) — a video loads/plays only when
  // it's the active one. See {@link LightboxVideo}.
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  // The attachment model carries no video dimensions, so measure each video's
  // intrinsic size to fit the lightbox stage to its aspect ratio (react-photo-view
  // draws a custom `render` slide at the raw dims, unfitted — see fitToViewport).
  const [videoDimensions, setVideoDimensions] = useState<
    Record<string, { width: number; height: number }>
  >({});

  useEffect(() => {
    const probes: HTMLVideoElement[] = [];
    for (const tile of tiles) {
      if (!isVideoTile(tile)) continue;
      const probe = document.createElement('video');
      probe.preload = 'metadata';
      probe.muted = true;
      probe.addEventListener('loadedmetadata', () => {
        const { videoWidth, videoHeight } = probe;
        if (!videoWidth || !videoHeight) return;
        setVideoDimensions(prev =>
          prev[tile.key]
            ? prev
            : {
                ...prev,
                [tile.key]: { width: videoWidth, height: videoHeight },
              },
        );
      });
      probe.src = tileMediaUrl(tile);
      probes.push(probe);
    }
    return () => {
      for (const probe of probes) {
        probe.removeAttribute('src');
        probe.load();
      }
    };
  }, [tiles]);

  // Every video's dimensions are known → remount the tiles ONCE (via the key
  // suffix below) so each PhotoView registers with its fitted stage. Remounting
  // all tiles together preserves gallery order (react-photo-view reads a custom
  // slide's dims only at mount and appends re-registered items to the end, which
  // would otherwise break the index-based video active-gating).
  const allVideosMeasured = tiles.every(
    tile => !isVideoTile(tile) || videoDimensions[tile.key],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft < maxScroll - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [tiles.length]);

  const nudge = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 140, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {/* One PhotoProvider per message → the message's media open as a single
          swipeable/zoomable full-screen gallery. The active slide is published
          via ActiveSlideContext so a video plays only while it's on screen. */}
      <ActiveSlideContext.Provider value={{ activeIndex, open: viewerOpen }}>
        <PhotoProvider
          onIndexChange={setActiveIndex}
          onVisibleChange={(visible, index) => {
            setViewerOpen(visible);
            setActiveIndex(index);
          }}
        >
          <div
            ref={scrollRef}
            className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {tiles.map((tile, index) =>
              tile.kind === 'remote' ? (
                <ChannelMessageAttachmentTile
                  key={`${tile.key}:${allVideosMeasured}`}
                  kind="remote"
                  index={index}
                  filename={tile.filename}
                  contentType={tile.contentType}
                  src={tile.url}
                  videoWidth={videoDimensions[tile.key]?.width}
                  videoHeight={videoDimensions[tile.key]?.height}
                />
              ) : (
                <ChannelMessageAttachmentTile
                  key={`${tile.key}:${allVideosMeasured}`}
                  kind="local"
                  filename={tile.filename}
                  contentType={tile.contentType}
                  src={tile.localPreviewUrl}
                  progress={tile.progress}
                  errorMessage={tile.errorMessage}
                />
              ),
            )}
          </div>
        </PhotoProvider>
      </ActiveSlideContext.Provider>
      {canScrollLeft ? (
        <ScrollNudgeButton direction="left" onClick={() => nudge(-1)} />
      ) : null}
      {canScrollRight ? (
        <ScrollNudgeButton direction="right" onClick={() => nudge(1)} />
      ) : null}
    </div>
  );
}

function ScrollNudgeButton({
  direction,
  onClick,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white text-black shadow-md transition hover:scale-105',
        direction === 'left' ? 'left-1' : 'right-1',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}
